import { enqueueSnackbar } from 'notistack';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { OperationPayload } from './types';

/**
 * Validates that an ROI has a valid texture with content.
 */
function validateROITexture(
    roi: RegionOfInterest,
    label: string
): { valid: boolean; error?: string } {
    if (!roi.texture) {
        return { valid: false, error: `${label} has no texture` };
    }
    const bb = roi.texture.contentBoundingBox;
    if (!bb) {
        return { valid: false, error: `${label} has invalid bounding box` };
    }
    const size = bb.size;
    if (size[0] <= 0 || size[1] <= 0 || size[2] <= 0) {
        return {
            valid: false,
            error: `${label} has invalid dimensions (may be empty)`,
        };
    }
    return { valid: true };
}

/**
 * Execute boolean operations on a target ROI.
 * All ROIs in the payload should be validated before calling this function.
 * Returns true if successful, false otherwise.
 */
export function executeBooleanOperations(
    payload: OperationPayload<RegionOfInterest>,
    resultROI: RegionOfInterest
): boolean {
    if (!payload?.steps || payload.steps.length === 0) {
        enqueueSnackbar('Cannot execute boolean operation: no steps provided', {
            variant: 'error',
        });
        return false;
    }

    const firstStep = payload.steps[0];
    if (firstStep.leftOperand === 'previous-result') {
        enqueueSnackbar(
            'Invalid operation: first step cannot use previous result',
            { variant: 'error' }
        );
        return false;
    }

    // Phase 1: validate all inputs before opening a modify entry.
    // This prevents a spurious undo entry when an operand texture is invalid.
    // The first operand's texture is validated here; subsequent inputROIs are
    // accumulated results that only exist after execution begins, so they are
    // validated implicitly by the try/catch in phase 2.
    const sourceROI = firstStep.leftOperand;
    const sourceValidation = validateROITexture(sourceROI, 'Input structure');
    if (!sourceValidation.valid) {
        enqueueSnackbar(sourceValidation.error!, { variant: 'error' });
        return false;
    }

    for (let stepIndex = 0; stepIndex < payload.steps.length; stepIndex++) {
        const step = payload.steps[stepIndex];
        if (step.rightOperands.length === 0) {
            enqueueSnackbar('Operation step has no right operands', {
                variant: 'error',
            });
            return false;
        }
        for (const secondROI of step.rightOperands) {
            const secondValidation = validateROITexture(
                secondROI,
                'Second operand'
            );
            if (!secondValidation.valid) {
                enqueueSnackbar(secondValidation.error!, { variant: 'error' });
                return false;
            }
        }
    }

    // Phase 2: execute all operations under a single modify/finish pair so
    // the entire boolean operation creates exactly one undo entry.
    const roiModify = resultROI.startModify('Boolean operation');
    try {
        for (let stepIndex = 0; stepIndex < payload.steps.length; stepIndex++) {
            const step = payload.steps[stepIndex];

            // For the first step, use the explicit source; for subsequent steps, use the result.
            const inputForStep: RegionOfInterest =
                stepIndex === 0 ? sourceROI : resultROI;

            for (let i = 0; i < step.rightOperands.length; i++) {
                const secondROI = step.rightOperands[i]!;
                const inputROI =
                    stepIndex === 0 && i === 0 ? inputForStep : resultROI;

                // SDFOperations handles all grid alignment internally using temp textures
                SDFOperations.applyBoolean(
                    inputROI.texture!,
                    secondROI.texture!,
                    step.operation,
                    resultROI
                );
            }
        }

        return true;
    } catch (operationError) {
        console.error('Boolean operation step failed:', operationError);
        enqueueSnackbar(
            `Operation failed: ${
                operationError instanceof Error
                    ? operationError.message
                    : String(operationError)
            }`,
            { variant: 'error' }
        );
        return false;
    } finally {
        roiModify.finish();
    }
}

/**
 * Compute the result of chained boolean operations as a new {@link ContourTexture}
 * without modifying any {@link RegionOfInterest}. Intended for preview rendering.
 *
 * The caller owns the returned texture and must call {@link ContourTexture.delete}
 * when it is no longer needed. Returns null if any required texture is missing.
 */
export function computeBooleanPreviewTexture(
    payload: OperationPayload<RegionOfInterest>
): ContourTexture | null {
    if (!payload.steps.length) {
        return null;
    }

    const firstStep = payload.steps[0];
    if (firstStep.leftOperand === 'previous-result') {
        return null;
    }

    let resultTexture: ContourTexture | null = null;

    for (let stepIndex = 0; stepIndex < payload.steps.length; stepIndex++) {
        const step = payload.steps[stepIndex];

        for (let i = 0; i < step.rightOperands.length; i++) {
            const rightROI = step.rightOperands[i];
            if (!rightROI.texture) {
                resultTexture?.delete();
                return null;
            }

            const isFirstOperation = stepIndex === 0 && i === 0;
            const src1Texture = isFirstOperation
                ? (step.leftOperand as RegionOfInterest).texture
                : resultTexture;
            if (!src1Texture) {
                resultTexture?.delete();
                return null;
            }
            const src1: ContourTexture = src1Texture;

            const newResult = SDFOperations.applyBooleanToNewTexture(
                src1,
                rightROI.texture,
                step.operation
            );

            // Replace the accumulated result, freeing the old one.
            // On the first operation src1 is borrowed from the source ROI — do not delete.
            if (!isFirstOperation && resultTexture) {
                resultTexture.delete();
            }

            resultTexture = newResult;
        }
    }

    return resultTexture;
}
